'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @primitive RadioCardGroup / RadioCard
 * ROLE: radiogroup + radio (card-shaped variant)
 * ARIA: aria-checked, aria-disabled; group needs aria-label or aria-labelledby,
 *       or a <Field> label (read through useFieldLabelling)
 * KEYBOARD: ArrowUp/ArrowDown/ArrowLeft/ArrowRight navigate; Tab exits; Space toggles
 * FOCUS: one tab stop per group (roving tabindex via Radix)
 * PRM: selection transition instant
 * FORCED-COLORS: border = ButtonText; selected border = Highlight; dot = HighlightText
 * RSC: CLIENT
 * SUBSTRATE: @radix-ui/react-radio-group
 *
 * Card-shaped variant of `RadioGroup` / `RadioGroupItem`. Use when each option
 * needs a title, optional description, and optional icon (e.g. "choose your
 * plan", "choose your visibility"). For simple list radios, prefer `RadioGroup`.
 *
 * `columns='auto'` (default) derives the grid template from `React.Children.count`
 * via container queries — single column on narrow containers, N columns on wide.
 * Pass a number to force a specific column count.
 */
import * as RadixRadioGroup from '@radix-ui/react-radio-group';
import { Children } from 'react';
import { useFieldLabelling } from '../forms/Field/FieldGroup';
import { Label } from '../../server/typography/Label/index';
import { Body } from '../../server/typography/Body/index';
import { IconTile } from '../../IconTile/IconTile';
import styles from './RadioCard.module.css';
export function RadioCardGroup({ value, onValueChange, disabled = false, columns = 'auto', fold = 'auto', children, ref, ...ariaProps }) {
    // Inside a <Field>, the Field label names the radiogroup unless the caller
    // names it.
    const labelling = useFieldLabelling();
    const namesItself = ariaProps['aria-label'] !== undefined || ariaProps['aria-labelledby'] !== undefined;
    // Auto-derived column count caps at 4 so a long list folds nicely instead of
    // collapsing to ultra-narrow cards on a 4K screen.
    const childCount = Children.count(children);
    const resolvedCols = columns === 'auto'
        ? Math.min(Math.max(childCount, 1), 4)
        : columns;
    return (_jsx(RadixRadioGroup.Root, { ref: ref, value: value, onValueChange: onValueChange, disabled: disabled, className: styles.group, "data-cols": resolvedCols, "data-fold": fold, "aria-describedby": labelling['aria-describedby'], "aria-invalid": ariaProps['aria-invalid'] === true || labelling['aria-invalid'] === true ? true : undefined, "aria-labelledby": namesItself ? ariaProps['aria-labelledby'] : labelling['aria-labelledby'], "aria-label": ariaProps['aria-label'], children: _jsx("div", { className: styles.grid, children: children }) }));
}
export function RadioCard({ value, title, sub, icon, disabled = false, }) {
    const hasSub = sub != null;
    const hasIcon = icon != null;
    return (_jsxs("label", { className: styles.card, "data-has-sub": hasSub ? 'true' : undefined, "data-has-icon": hasIcon ? 'true' : undefined, children: [_jsx(RadixRadioGroup.Item, { value: value, disabled: disabled, className: styles.srRadio }), hasIcon ? (_jsx("span", { className: styles.iconSlot, children: _jsx(IconTile, { tone: "burgundy", size: 32, children: icon }) })) : null, _jsxs("span", { className: styles.body, children: [_jsx(Label, { children: title }), hasSub ? (_jsx(Body, { size: "sm", color: "mute", children: sub })) : null] })] }));
}
//# sourceMappingURL=RadioCard.js.map